import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { createAbsoluteUrl } from "utils/constants";
import { useGlobalSlice } from "app/slice";
import { selectCompatibilities } from "app/slice/selectors";
import { toast } from "app/components/ui/use-toast";
import { RootState } from "types";
import { UpgradeSecurityPlan } from "./upgrade-security-plan";

import { Label } from "app/components/ui/label";
import { Button } from "app/components/ui/button";
import { Icons } from "app/components/ui/icons";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "app/components/ui/select";

interface Props {
  activeLines: number;
  verifyData: any;
  verifyLoading: boolean;
}

// Dynamic addMoreLineOptions based on available lines
const getAddMoreLineOptions = (totalLines: number) => {
  const maxLines = 5;
  const availableLines = maxLines - totalLines;
  
  if (availableLines <= 0) return [];
  
  return Array.from({ length: availableLines }, (_, index) => ({
    value: index + 1,
    label: (index + 1).toString()
  }));
};

const planOptions = (plan: number) => {
  return [
    { value: 1, label: "Basic Individual +$10/month" },
    { value: 2, label: "Basic Family +$20/month" },
    { value: 3, label: "Complete Individual +$10/month" },
    { value: 4, label: "Complete Family +$20/month" },
  ];
};

export const MobileAccount = ({ activeLines, verifyData, verifyLoading }: Props) => {
  const navigate = useNavigate();
  const [isAddLine, setIsAddLine] = React.useState(false);
  const compatibilitiesData = useSelector(selectCompatibilities);
  const [addLineSelect, setAddLineSelect] = React.useState(false);
  const [lines, setLines] = useState<number>(1);
  const [hasLineSelected, setHasLineSelected] = useState(false);
  const [isUpgradePlan, setIsUpgradePlan] = useState(false);
  const [plan, setPlan] = useState<number>(1);
  const [addPlanSelect, setAddPlanSelect] = useState(false);
  const [hasPlanSelected, setHasPlanSelected] = useState(false);

  const dispatch = useDispatch();
  const { actions, useGetNewLineQuery, useLazyGetDigitalSecurityCurrentAndUpgradePlansQuery } = useGlobalSlice();
  const user = useSelector((state: RootState) => state.global?.user);
  const [getNewLineParams, setGetNewLineParams] = useState<any>(null);
  const { data: newLineData, error: newLineError, isLoading: newLineLoading } = useGetNewLineQuery(getNewLineParams, {
    skip: !getNewLineParams,
  });

  // Digital Security Plans Query
  const [getDigitalSecurityPlans, { 
    data: digitalSecurityData, 
    isLoading: digitalSecurityLoading, 
    isSuccess: digitalSecuritySuccess, 
    isError: digitalSecurityError, 
    error: digitalSecurityErrorData 
  }] = useLazyGetDigitalSecurityCurrentAndUpgradePlansQuery();

  // Fetch digital security plans when component mounts
  useEffect(() => {
    if (user?._id) {
      getDigitalSecurityPlans({ customerId: user._id });
    }
  }, [user?._id, getDigitalSecurityPlans]);

  // Log digital security data when it changes
  useEffect(() => {
    if (digitalSecuritySuccess && digitalSecurityData) {
      console.log('Mobile Account - Digital Security Data:', digitalSecurityData);
      // Store in global state
      dispatch(actions.setDigitalSecurityCurrentAndUpgradePlans(digitalSecurityData));
    }
    if (digitalSecurityError) {
      console.error('Mobile Account - Digital Security Error:', digitalSecurityErrorData);
    }
  }, [digitalSecuritySuccess, digitalSecurityData, digitalSecurityError, digitalSecurityErrorData, dispatch, actions]);

  // Update line state when activeLines changes to ensure it stays within valid bounds
  useEffect(() => {
    if (activeLines > 0) {
      const availableOptions = getAddMoreLineOptions(activeLines);
      if (availableOptions.length === 0) {
        setLines(1); // Reset to 1 if no lines available
      } else if (lines > availableOptions.length) {
        setLines(availableOptions.length); // Cap at maximum available
      }
    }
  }, [activeLines, lines]);

  const submitLinesData = () => {
    console.log("clicking");
    
    if (!user?._id) {
      toast({
        title: "Error",
        description: "User information not found. Please login again.",
        variant: "error",
      });
      return;
    }

    const params = {
      line: lines,
      customerId: user._id,
    };
    
    console.log("Calling getNewLine with params:", params);
    setGetNewLineParams(params);
  };

  useEffect(() => {
    if (activeLines === 5) {
      toast({
        title: "Lines Limit",
        description: "You have already used your available lines.",
        variant: "error",
      });
    }
  }, [activeLines]);

  useEffect(() => {
    if (newLineError) {
      toast({
        title: "Error",
        description: "Failed to get new line. Please try again.",
        variant: "error",
      });
      setGetNewLineParams(null);
    }
  }, [newLineError]);

  useEffect(() => {
    if (newLineData && !newLineLoading) {
      dispatch(actions.setLines({ numberOfLines: lines }));
      dispatch(actions.setAddLinesData(newLineData.data));
      console.log("lines dispatched", lines);
      console.log("addLinesData saved:", newLineData.data);
      
      const navigationState = {
        from: "multiNetworkFlow",
        getNewLineData: newLineData,
        multiNetworkPlanId: newLineData.data?.multinetworkPlanId,
        compatibilitiesData: compatibilitiesData
      };
      
      console.log("=== MOBILE ACCOUNT NAVIGATION DEBUG ===");
      console.log("Navigating to /multi-network-phone-type with state:", navigationState);
      console.log("getNewLineData:", newLineData);
      console.log("getNewLineData.data:", newLineData.data);
      console.log("multiNetworkPlanId:", newLineData.data?.multinetworkPlanId);
      console.log("=======================================");
      
      navigate("/multi-network-phone-type", {
        state: navigationState
      });
      setGetNewLineParams(null);
    }
  }, [newLineData, newLineLoading, lines, dispatch, actions, navigate]);

  return (
    <React.Fragment>
      <div className="">
        <div className="">
          <h4 className="mb-4 text-xl font-bold leading-5 text-black sm:text-[1.35rem] lg:text-2xl sm:leading-6 lg:leading-7 font-mobile sm:mb-6 lg:mb-9">
            VLE Mobile Account
          </h4>
          <div className="space-y-4 sm:space-y-[1.125rem]">
            <div className="space-y-1.5 sm:space-y-2 md:space-y-2.5">
              <div className="flex justify-between items-center">
                <h4 className="text-[#1D1D1B] text-sm sm:text-[0.935rem] lg:text-base font-semibold leading-6">
                  Active Lines:
                </h4>
                <p className="text-[#1D1D1B] text-sm sm:text-[0.935rem] lg:text-base font-semibold leading-6">
                  {activeLines}/5 {hasLineSelected && lines > 0 && `(+${lines}) lines added`}
                </p>
              </div>
              {!isAddLine && activeLines < 5 && getAddMoreLineOptions(activeLines).length > 0 && (
                <div className="flex justify-end">
                  <span
                    className="inline-block text-[0.7rem] sm:text-xs font-medium leading-4 sm:leading-5 text-[#FE4D01] cursor-pointer underline decoration-solid decoration-skip-ink decoration-[auto] underline-offset-[auto] [text-underline-position:from-font]"
                    onClick={() => {
                      setIsAddLine(true);
                      setHasLineSelected(false);
                    }}
                  >
                    Add Lines To Account
                  </span>
                </div>
              )}
              {isAddLine && activeLines < 5 && getAddMoreLineOptions(activeLines).length > 0 && (
                <div className="pb-2.5">
                  <Label
                    id="addLines"
                    className="w-fit text-black text-xs sm:text-[0.825rem] md:text-sm font-medium leading-4 md:leading-5 mb-1 md:mb-[0.375rem]"
                  >
                    Select Number of Line
                  </Label>
                  <Select
                    value={lines.toString()}
                    onValueChange={(val) => {
                      const newLineCount = parseInt(val);
                      setLines(newLineCount);
                      setAddLineSelect(false);
                      setIsAddLine(false);
                      setHasLineSelected(true);
                      
                      // Update Redux state with new line count
                      dispatch(actions.setLines({ numberOfLines: newLineCount }));
                    }}
                    onOpenChange={setAddLineSelect}
                  >
                    <SelectTrigger className="bg-white hover:bg-[#FAFAFA] w-full h-10 md:h-[2.875rem] py-[0.625rem] pl-[0.625rem] pr-[0.75rem] sm:py-[0.6875rem] sm:pl-[0.75rem] sm:pr-[0.875rem] md:py-[0.78125rem] md:pl-[0.84375rem] md:pr-[0.9375rem] border border-[#D1D5DB] rounded-xl text-black text-[0.825rem] sm:text-sm font-medium leading-4 md:leading-5">
                      <SelectValue
                        placeholder={
                          <span className="text-black/50">Select Line</span>
                        }
                      />
                      <img
                        src="/assets/chevron-down.svg"
                        className={`transition-transform duration-300 ease-in-out ${
                          addLineSelect ? "rotate-180" : ""}`}
                        alt="Chevron"
                      />
                    </SelectTrigger>
                    <SelectContent className="max-h-[20rem]">
                      <SelectGroup>
                        {getAddMoreLineOptions(activeLines).map((option) => (
                          <SelectItem
                            key={option.value}
                            value={option.value.toString()}
                            className="text-[0.825rem] sm:text-sm font-normal leading-4 md:leading-5 text-black"
                          >
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="bg-[#E5E7EB] w-full h-px"></div>
              <div className="flex justify-between items-center">
                <h4 className="text-[#1D1D1B] text-sm sm:text-[0.935rem] lg:text-base font-semibold leading-6">
                  Security Plan:
                </h4>
                <p className="text-[#1D1D1B] text-sm sm:text-[0.935rem] lg:text-base font-semibold leading-6">
                  {verifyData?.data?.securityPlan?.securityPlanName}
                </p>
              </div>
              <UpgradeSecurityPlan />
              <div className="bg-[#F5F5F5] p-3 sm:p-[0.875rem] rounded-xl">
                <p className="text-black text-[0.725rem] sm:text-xs font-medium leading-4 sm:leading-5 mb-1 sm:mb-[0.375rem]">
                  Here add a dropdown menu with the number of avialable lines for the
                  account.
                </p>
                {/* <Link
                  to="/support-tickets/submit-ticket"
                  className="text-[0.7rem] sm:text-xs font-medium leading-4 sm:leading-5 text-[#FE4D01] underline decoration-solid decoration-skip-ink decoration-[auto] underline-offset-[auto] [text-underline-position:from-font]"
                >
                  Create Support Ticket
                </Link> */}
                <a
                  href={createAbsoluteUrl("/support-tickets/submit-ticket")}
                  target="_self"
                  rel="noopener noreferrer"
                  className="text-[0.7rem] sm:text-xs font-medium leading-4 sm:leading-5 text-[#FE4D01] underline decoration-solid decoration-skip-ink decoration-[auto] underline-offset-[auto] [text-underline-position:from-font]"
                >
                  Create Support Ticket
                </a>
              </div>
            </div>
            {activeLines >= 5 && (
              <div className="hidden sm:block bg-[#F5F5F5] p-3 sm:p-[0.875rem] rounded-xl">
                <p className="text-black text-[0.725rem] sm:text-xs font-medium leading-4 sm:leading-5 mb-1 sm:mb-[0.375rem]">
                  You have used all lines allowed for your account. Please contact customer service for assistance.
                </p>
                {/* <Link
                  to="/support-tickets/submit-ticket"
                  className="text-[0.7rem] sm:text-xs font-medium leading-4 sm:leading-5 text-[#FE4D01] underline decoration-solid decoration-skip-ink decoration-[auto] underline-offset-[auto] [text-underline-position:from-font]"
                >
                  Create Support Ticket
                </Link> */}
                <a
                  href={createAbsoluteUrl("/support-tickets/submit-ticket")}
                  target="_self"
                  rel="noopener noreferrer"
                  className="text-[0.7rem] sm:text-xs font-medium leading-4 sm:leading-5 text-[#FE4D01] underline decoration-solid decoration-skip-ink decoration-[auto] underline-offset-[auto] [text-underline-position:from-font]"
                >
                  Create Support Ticket
                </a>
              </div>
            )}
          </div>
        </div>
        {activeLines >= 5 && (
          <div className="sm:hidden bg-[#F5F5F5] p-3 rounded-xl mb-[1.125rem]">
            <p className="text-black text-[0.725rem] font-medium leading-4 mb-1">
              You have used all lines allowed for your account. Please contact customer service for assistance.
            </p>
            {/* <Link
              to="/support-tickets/submit-ticket"
              className="text-[0.7rem] font-medium leading-4 text-[#FE4D01] underline decoration-solid decoration-skip-ink decoration-[auto] underline-offset-[auto] [text-underline-position:from-font]"
            >
              Create Support Ticket
            </Link> */}
            <a
              href={createAbsoluteUrl("/support-tickets/submit-ticket")}
              target="_self"
              rel="noopener noreferrer"
              className="text-[0.7rem] font-medium leading-4 text-[#FE4D01] underline decoration-solid decoration-skip-ink decoration-[auto] underline-offset-[auto] [text-underline-position:from-font]"
            >
              Create Support Ticket
            </a>
          </div>
        )}
      </div>
      {activeLines < 5 && (
        <Button
          type="button"
          variant="primary"
          onClick={() => {
            console.log("clicked");
            submitLinesData();
          }}
          disabled={activeLines >= 5 || newLineLoading}
        >
          {newLineLoading ? (
            <div className="flex items-center gap-0.5 sm:gap-1">
              <Icons.Spinner className="animate-spin size-4 md:size-5" />
              <span>Verifying...</span>
            </div>
          ) : (
            "Continue"
          )}
        </Button>
      )}
    </React.Fragment>
  );
};
